import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { fetchConnections, fetchParameters, fetchWorkflows } from '@/services/logicAppsApi';
import { AutomationAppUIModel } from '@/types/automation';
import { useMemo } from 'react';
import { useAppStore } from '@/stores/appStore';
import { AppContext } from '@/services/appClient';
import { getLocalAppContext, isLocalMode } from '@/services/localRuntimeClient';
import { getLogicEndpoint } from '@/services/endpoints';

type FetchFn<T> = (params: AppContext) => Promise<T[]>;

export type AppResourceResult<T> = {
  data: T[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isFetching: boolean;
  refetch: () => void;
};


/**
 * Generic hook that fetches a resource type for a single app.
 *
 * Uses the currently selected app from appStore via useAppDetails for hostname resolution.
 * For fetching resources across multiple apps, use useMultiAppResource instead.
 *
 * @param app - The automation app:
 *   - **AutomationAppUIModel**: fetch for that app.
 *   - **`undefined`**: fall back to the Zustand store's `selectedApp`/
 *     `selectedProject` (legacy behavior — used when callers don't yet know
 *     which app they want).
 *   - **`null`**: explicitly "no app, do not fetch". The store fallback is
 *     bypassed. URL-driven callers (e.g. HeaderNavigation, useNavCommands)
 *     should pass `null` when the URL has no app context, so workflow
 *     fetches don't leak across routes via stale Zustand state.
 * @param queryKeyPrefix - Cache-key prefix (e.g. 'workflows', 'connections')
 * @param fetchFn - The service function that fetches resources for one app
 */
function useAppResource<T>(
  app: AutomationAppUIModel | null | undefined,
  queryKeyPrefix: string,
  fetchFn: FetchFn<T>,
): AppResourceResult<T> {
  const { getOttoToken, isAuthenticated } = useAuth();
  const { selectedApp, selectedProject } = useAppStore();
  const endpoint = getLogicEndpoint();
  // `null` means the caller explicitly opted out of store fallback (e.g.
  // URL-driven hooks signalling "no app context"). `undefined` keeps the
  // legacy behavior of falling back to the Zustand selection.
  const explicitlyDisabled = app === null;
  const projectName = useMemo(
    () => (explicitlyDisabled ? '' : app?.projectName ?? selectedProject ?? ''),
    [explicitlyDisabled, app, selectedProject],
  );
  const appName = useMemo(
    () => (explicitlyDisabled ? '' : app?.name ?? selectedApp?.name ?? ''),
    [explicitlyDisabled, app, selectedApp],
  );

  const queryEnabled =
    !explicitlyDisabled &&
    (isAuthenticated || isLocalMode()) &&
    !!projectName &&
    !!appName &&
    !!endpoint;

  const query: UseQueryResult<T[], Error> = useQuery({
    queryKey: [queryKeyPrefix, projectName.toLowerCase(), appName.toLowerCase()],
    queryFn: async (): Promise<T[]> => {
      if (isLocalMode()) {
        const localCtx = getLocalAppContext();
        return fetchFn({
          projectName: 'dummy-project',
          appName: localCtx.appName,
          getToken: async () => 'dummy-key',
        });
      }
      
      return fetchFn({
        projectName: projectName,
        appName: appName,
        getToken: getOttoToken,
      });
    },
    enabled: queryEnabled,
    staleTime: 5 * 60 * 1000,
  });

  // While prereqs aren't resolved yet (project/app not yet hydrated, or auth
  // not ready), the query is disabled and react-query reports `isLoading: false`.
  // From the caller's perspective this is misleading — the caller passed an
  // app and is genuinely waiting for data — so surface the disabled-prereq
  // state as loading too. Errored queries are intentionally excluded so
  // failures surface instead of being masked as perpetual loading.
  const isWaitingForPrereqs =
    !!app && !queryEnabled && !query.isError;

  return {
    data: query.data ?? [],
    isLoading: query.isLoading || isWaitingForPrereqs,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,
    refetch: () => void query.refetch(),
  };
}

export const useAppWorkflows = (app: AutomationAppUIModel | null | undefined) => useAppResource(app, 'workflows', fetchWorkflows);
export const useAppConnections = (app: AutomationAppUIModel | null | undefined) => useAppResource(app, 'connections', fetchConnections);
export const useAppParameters = (app: AutomationAppUIModel | null | undefined) => useAppResource(app, 'parameters', fetchParameters);
